import './styles.scss';
import { AppContext } from '../../../../context';
import { BrandString, EventTemplate, Interview, InterviewStatus, MeetIntro } from '../../../../types';
import { Button } from '@goodtimeio/rain';
import { Duration } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelopeOpen } from '@fortawesome/pro-regular-svg-icons';
import { mapAvailableTimeRangeToTimeRange, mapEventTemplateToTimeRange } from '../../../../utils/date-utils';
import { useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import ItineraryCalendarDisplay from '../ItineraryCalendarDisplay';

interface ConfirmationCardProps {
    interview: Interview | undefined;
    primaryColor: string;
    textColor: string;
    recruiterEmail: string;
    hash: string;
    canGuestReschedule: boolean;
}

interface InterviewStatusCard {
    interview: Interview;
    amountOfMeeting: string;
    durationTimeOfMeeting: string;
    recruiterEmail: string;
    primaryColor: string;
    primaryTextColor: string;
    canGuestReschedule: boolean;
    translate: (val: string, dynamicValueObject?: Record<string, unknown>) => string;
    onReschedule?: () => void;
    onUpdateAvailability?: () => void;
    meetIntro?: MeetIntro;
    getValueForBrandStringField: (brandStringArray: string | BrandString[] | undefined, keyToCheck?: string) => string;
}

const ConfirmationCard: React.FC<ConfirmationCardProps> = ({
    interview,
    primaryColor,
    textColor,
    recruiterEmail,
    hash,
    canGuestReschedule,
}) => {
    const history = useHistory();
    const { meetIntro, getValueForBrandStringField, translate, addTimeSlot, addDays } = useContext(AppContext);

    useEffect(() => {
        if (primaryColor) {
            document.documentElement.style.setProperty('--primary-date-picker-color', primaryColor);
        }
        if (textColor) {
            document.documentElement.style.setProperty('--primary-text-color', textColor);
        }
    }, [primaryColor, textColor]);

    if (!interview) {
        return null;
    }

    const { status } = interview;

    const getAmountOfMeeting = () => {
        return translate('general:meeting', { count: interview.events.length });
    };

    const getDurationTimeOfMeeting = () => {
        const totalMin = interview.events.reduce(
            (acc: number, currentEvent: EventTemplate) => acc + currentEvent.duration,
            0,
        );
        const totalMill = totalMin * 60000;
        const duration = Duration.fromMillis(totalMill).shiftTo('hours', 'minutes');
        return duration.hours
            ? translate('confirm:hoursTotalTime', { count: duration.hours })
            : translate('confirm:minutesTotalTime', { count: duration.minutes });
    };
    const onReschedule = () => {
        return history.push(`/reschedule/${hash}`);
    };
    const onUpdateAvailability = () => {
        addTimeSlot('');
        addDays([]);
        return history.push(`/pickDays/${hash}`);
    };

    return status === InterviewStatus.CONFIRMED ||
        status === InterviewStatus.CONFLICT ||
        status === InterviewStatus.RESCHEDULE ? (
        <ConfirmedState
            interview={interview}
            amountOfMeeting={getAmountOfMeeting()}
            durationTimeOfMeeting={getDurationTimeOfMeeting()}
            translate={translate}
            recruiterEmail={recruiterEmail}
            primaryColor={primaryColor}
            primaryTextColor={textColor}
            onReschedule={onReschedule}
            canGuestReschedule={canGuestReschedule}
            meetIntro={meetIntro?.data}
            getValueForBrandStringField={getValueForBrandStringField}
        />
    ) : (
        <PendingState
            interview={interview}
            amountOfMeeting={getAmountOfMeeting()}
            durationTimeOfMeeting={getDurationTimeOfMeeting()}
            translate={translate}
            recruiterEmail={recruiterEmail}
            primaryColor={primaryColor}
            primaryTextColor={textColor}
            onUpdateAvailability={onUpdateAvailability}
            canGuestReschedule={canGuestReschedule}
            getValueForBrandStringField={getValueForBrandStringField}
        />
    );
};

const ConfirmedState: React.FC<InterviewStatusCard> = ({
    interview,
    amountOfMeeting,
    durationTimeOfMeeting,
    translate,
    recruiterEmail,
    primaryColor,
    primaryTextColor,
    onReschedule,
    canGuestReschedule,
    meetIntro,
    getValueForBrandStringField,
}) => {
    return (
        <div className="cardContainer">
            <div className="cardContent">
                <h1 className="title">
                    {getValueForBrandStringField(
                        meetIntro?.meetInterviewConfirmedHeader,
                        'confirm:interviewConfirmed',
                    ) || translate('confirm:interviewConfirmed')}
                </h1>
                {interview?.settings?.needsReview ? null : (
                    <div className="emailInfo">
                        <p>
                            <FontAwesomeIcon icon={faEnvelopeOpen} /> {translate('confirm:copyHasBeenSentTo')}:{' '}
                            <span className="email"> {recruiterEmail}</span>
                        </p>
                    </div>
                )}
                <p className="confirmationMsg">
                    {getValueForBrandStringField(
                        meetIntro?.meetInterviewConfirmedMessage,
                        'confirm:meetingHasBeenScheduled',
                    ) || translate('confirm:meetingHasBeenScheduled')}
                </p>
                <h3 className="itineraryTitle">
                    {getValueForBrandStringField(meetIntro?.meetItineraryHeader, 'confirm:yourInterviewItinerary') ||
                        translate('confirm:yourInterviewItinerary')}
                </h3>

                <p className="amountMeeting">
                    {amountOfMeeting}, {durationTimeOfMeeting}{' '}
                </p>
                <ItineraryCalendarDisplay
                    timeRanges={interview.events.map(mapEventTemplateToTimeRange)}
                    primaryColor={primaryColor}
                    primaryTextColor={primaryTextColor}
                    interview={interview}
                />
            </div>
            <div className="cardFooter">
                {canGuestReschedule ? (
                    <Button onClick={onReschedule} className="rescheduleButton">
                        {translate('confirm:iNeedToReschedule')}
                    </Button>
                ) : null}
            </div>
        </div>
    );
};

const PendingState: React.FC<InterviewStatusCard> = ({
    interview,
    amountOfMeeting,
    durationTimeOfMeeting,
    translate,
    recruiterEmail,
    primaryColor,
    primaryTextColor,
    onUpdateAvailability,
    canGuestReschedule,
}) => {
    return (
        <div className="cardContainer">
            <div className="cardContent">
                <h1 className="pendingTitle">{translate('confirm:thanksForSharingYourAvailability')}</h1>
                <p className="confirmationMsg">{translate('confirm:wellGetBackToYou')}</p>
                <h3 className="itineraryTitle">{translate('confirm:availabilitySent')}</h3>

                <p className="amountMeeting">
                    {amountOfMeeting}, {durationTimeOfMeeting}{' '}
                </p>
                <ItineraryCalendarDisplay
                    timeRanges={(interview.settings.availableTimeRanges ?? []).map(mapAvailableTimeRangeToTimeRange)}
                    primaryColor={primaryColor}
                    primaryTextColor={primaryTextColor}
                    interview={interview}
                />

                {interview?.settings?.needsReview ? null : (
                    <div className="emailInfo">
                        <p>
                            <FontAwesomeIcon icon={faEnvelopeOpen} /> {translate('confirm:copyHasBeenSentTo')}:{' '}
                            <span className="email"> {recruiterEmail}</span>
                        </p>
                    </div>
                )}
            </div>

            <div className="cardFooter">
                {canGuestReschedule || interview.status === InterviewStatus.PENDING_REVIEW ? (
                    <Button onClick={onUpdateAvailability} className="rescheduleButton">
                        {translate('confirm:updateAvailability')}
                    </Button>
                ) : null}
            </div>
        </div>
    );
};

export default ConfirmationCard;
